import colors from 'shared/colors';
import { PROGRESS_OVER_TIME_AGGREGATORS, SNAPSHOT_AGGREGATORS, TIME_SERIES_AGGREGATORS } from '@/lib/constants';
import { aggregatorType, bucketType, chartGroupByOption, coloringMode, gridPageChartColorScheme, gridPageChartPrecision, gridPageChartType, optionColor, propertyType, timeSeriesInterval } from 'shared/constants.json';
import { displayName as displayNameOf } from 'shared/composables/user';
import { equalP } from 'shared/lib/array/array';
import { goal as goalConfig } from 'shared/api/query/configs.json';
import { isEqual } from 'lodash-es';
import { textByLang } from 'shared/lib/language';

const addLeadingZero = (value) => value.toString().padStart(2, '0');

// Parse ISO date string as local date to avoid timezone issues
// new Date('2024-01-15') parses as UTC, causing day shifts in western timezones
const parseLocalDate = (dateString) => {
  if (dateString === undefined || dateString === null || dateString === '') {
    return null;
  }
  const [year, month, day] = dateString.split('T')[0].split('-').map(Number);
  return new Date(year, month - 1, day);
};

export const getDayFromISOString = (dateString) => {
  const date = parseLocalDate(dateString);
  if (date === null) return null;
  return `${date.getFullYear()}-${addLeadingZero(date.getMonth() + 1)}-${addLeadingZero(date.getDate())}`;
};

export const getWeekFromISOString = (dateString) => {
  const date = parseLocalDate(dateString);
  if (date === null) return null;
  // ISO 8601: week 1 contains the first Thursday, so we find the nearest Thursday
  date.setDate(date.getDate() + 4 - (date.getDay() || 7));
  const yearStart = new Date(date.getFullYear(), 0, 1);
  const weekNumber = Math.ceil((((date - yearStart) / 86400000) + 1) / 7);
  return `${date.getFullYear()}-W${addLeadingZero(weekNumber)}`;
};

export const getMonthFromISOString = (dateString) => {
  const date = parseLocalDate(dateString);
  if (date === null) return null;
  return `${date.getFullYear()}-${addLeadingZero(date.getMonth() + 1)}`;
};

export const getYearFromISOString = (dateString) => {
  const date = parseLocalDate(dateString);
  if (date === null) return null;
  return date.getFullYear();
};

export const getQuarterFromISOString = (dateString) => {
  const date = parseLocalDate(dateString);
  if (date === null) return null;
  const quarter = Math.ceil((date.getMonth() + 1) / 3);
  return `${date.getFullYear()}-Q${quarter}`;
};

export const edgeName = (groupBy, t, userLang) => {
  if (!groupBy.isProperty) {
    return t(`edges.${groupBy.edge}`);
  }
  if (!groupBy.property?.label) {
    return '';
  }
  return textByLang(groupBy.property.label, userLang);
};

export const roundNumber = (number, precision = 2) => (number === null ? 0 : Number(number.toFixed(precision)));

export const isDateGrouping = (groupBy) => [
  chartGroupByOption.day,
  chartGroupByOption.week,
  chartGroupByOption.month,
  chartGroupByOption.quarter,
  chartGroupByOption.year,
].includes(groupBy);

const isEnumerableGrouping = (groupBy) => {
  if (groupBy?.groupBy !== chartGroupByOption.unique) {
    return false;
  }
  if (groupBy.isProperty === false) {
    if (groupBy.edge === goalConfig.edges.goalCycle) {
      return true;
    }
    return [propertyType.user, propertyType.space].includes(groupBy.edgeType);
  }
  const { type } = groupBy.property ?? {};
  return [propertyType.space, propertyType.user, propertyType.status, propertyType.options, propertyType.singleSelect].includes(type);
};

const isBucketGrouping = (groupBy) => groupBy?.groupBy === chartGroupByOption.bucket
  && [bucketType.custom, bucketType.automatic].includes(groupBy?.customBucket?.bucketType);

export const canFillEmptyValues = (groupBy) => isDateGrouping(groupBy?.groupBy)
  || isEnumerableGrouping(groupBy)
  || isBucketGrouping(groupBy);

export const seriesChartTypes = [gridPageChartType.bar, gridPageChartType.column, gridPageChartType.line];

export const chartHasMultipleSeries = (chart) => seriesChartTypes.includes(chart.chartType)
  && chart.aggregationConfig.stackOptions.groupBy !== chartGroupByOption.none;

const PROGRESS_EDGE = {
  capped: goalConfig.edges.cachedCalculatedCurrentBounded,
  uncapped: goalConfig.edges.cachedCalculatedCurrent,
};

const PROGRESS_OVER_TIME_AGGREGATOR = {
  capped: aggregatorType.progressOverTime,
  uncapped: aggregatorType.progressOverTimeUnbounded,
};

const isProgressOverTime = (aggregation) => PROGRESS_OVER_TIME_AGGREGATORS.includes(aggregation?.aggregator);

export const isProgressAggregation = (aggregation) => {
  if (isProgressOverTime(aggregation)) {
    return true;
  }
  return aggregation?.isProperty === false
    && SNAPSHOT_AGGREGATORS.includes(aggregation.aggregator)
    && Object.values(PROGRESS_EDGE).includes(aggregation.edge);
};

export const isBoundedProgress = (aggregation) => (isProgressOverTime(aggregation)
  ? aggregation.aggregator === PROGRESS_OVER_TIME_AGGREGATOR.capped
  : aggregation?.edge === PROGRESS_EDGE.capped);

export const withBoundedProgress = (aggregation, bounded) => {
  const variant = bounded ? 'capped' : 'uncapped';
  return isProgressOverTime(aggregation)
    ? { ...aggregation, aggregator: PROGRESS_OVER_TIME_AGGREGATOR[variant] }
    : { ...aggregation, edge: PROGRESS_EDGE[variant] };
};

// The two progress edges are one ref as far as the chart editor is concerned —
// the "cap at 100%" switch decides which of them an aggregation stores — so they
// share a key.
const canonicalEdge = (edge) => (edge === PROGRESS_EDGE.capped ? PROGRESS_EDGE.uncapped : edge);

// Canonical identity key for a chart ref (groupBy / aggregation / stackOptions).
// Dynamic property refs key on the property's uid; direct refs key on the edge
// name. Returns null when the ref carries no identifying field — e.g. a
// groupBy of `{ groupBy: 'none' }` or a count aggregator with no property.
export const chartRefKey = (ref) => {
  if (!ref) return null;
  if (ref.isProperty === true) return ref.property?.uid != null ? `p:${ref.property.uid}` : null;
  if (ref.isProperty === false) return ref.edge ? `e:${canonicalEdge(ref.edge)}` : null;
  return null;
};

// Set of canonical keys for a list of chart refs — the membership table for
// orphan-ref checks.
export const knownChartRefKeys = (refs) => new Set(
  refs.map(chartRefKey).filter((key) => key !== null),
);

// True when `ref` points at a property that no longer exists. Covers:
//   - dynamic deletion: `{ isProperty: true, property: undefined | { uid: orphan } }` — uid is gone or unknown
//   - direct removal: `{ isProperty: false, edge: 'gone' }` — edge isn't in lib/<entity>/properties.js anymore
//
// Modes that don't reference a property are never orphan. The backend doesn't
// clear `edge` / `edgeType` / `property` when the aggregator or groupBy is
// changed to a no-property mode, so a count aggregator can persist as
// `{ isProperty: false, edge: 'goalCycle', aggregator: 'count' }` long after
// the original sum/avg was reconfigured — the lingering edge isn't read by
// the chart, so it shouldn't trigger an orphan signal.
export const isOrphanChartRef = (ref, knownKeys) => {
  if (!ref) return false;
  if (ref.aggregator === aggregatorType.count) return false;
  if (TIME_SERIES_AGGREGATORS.includes(ref.aggregator)) return false;
  if (ref.groupBy === chartGroupByOption.none) return false;
  const key = chartRefKey(ref);
  if (key === null) return ref.isProperty === true;
  return !knownKeys.has(key);
};

export const fillMissingCategories = (categories, groupBy, userLang, excludeNullValues, context = {}) => {
  if (excludeNullValues) {
    return categories;
  }
  if (isDateGrouping(groupBy.groupBy)) {
    return fillMissingDateCategories(categories, groupBy, userLang);
  }
  if (isEnumerableGrouping(groupBy)) {
    return fillMissingEnumerableCategories(categories, groupBy, userLang, context);
  }
  if (isBucketGrouping(groupBy)) {
    return fillMissingBucketCategories(categories, context.buckets);
  }
  return categories;
};

const fillMissingBucketCategories = (categories, buckets) => {
  if (!Array.isArray(buckets) || buckets.length === 0) {
    return categories;
  }
  const existingMap = new Map(categories.map((c) => [c.originalValue, c]));
  const result = [];
  buckets.forEach((bucket, index) => {
    const existing = existingMap.get(bucket.label);
    if (existing) {
      result.push(existing);
      return;
    }
    const isUnderflowSentinel = bucket.min === undefined;
    if (isUnderflowSentinel) {
      return;
    }
    result.push({
      title: bucket.label,
      originalValue: bucket.label,
      bucketMin: bucket.min,
      bucketMax: bucket.max,
      bucketIndex: index,
    });
  });
  return result;
};

const fillMissingDateCategories = (categories, groupBy, userLang) => {
  if (categories.length < 2) {
    return categories;
  }

  const first = categories[0].originalValue;
  const last = categories[categories.length - 1].originalValue;
  const allDates = generateDateRange(first, last, groupBy.groupBy);
  const existingMap = new Map(categories.map((c) => [String(c.originalValue), c]));

  return allDates.map((date) => {
    const dateStr = String(date);
    if (existingMap.has(dateStr)) {
      return existingMap.get(dateStr);
    }
    return {
      title: getDisplayValue(date, undefined, groupBy, userLang),
      originalValue: date,
    };
  });
};

const fillMissingEnumerableCategories = (categories, groupBy, userLang, context) => {
  const allValues = getAllEnumerableValues(groupBy, userLang, context);
  if (allValues === null) {
    return categories;
  }
  return allValues;
};

const getAllEnumerableValues = (groupBy, userLang, context) => {
  const { goalCycles = [], spaces = [], users = [], leafStatuses = [], getColor = () => undefined } = context;

  if (groupBy.isProperty === false) {
    if (groupBy.edge === goalConfig.edges.goalCycle) {
      return goalCycles.map((gc) => ({
        title: gc.title,
        originalValue: gc.uid,
        color: getColor(gc.color),
        hasEntityColor: true,
        start: gc.start,
        end: gc.end,
      }));
    }
    if (groupBy.edgeType === propertyType.user) {
      return users.map((u) => ({
        title: displayNameOf(u),
        originalValue: u.uid,
        color: getColor(null),
      }));
    }
    if (groupBy.edgeType === propertyType.space) {
      return spaces.map((s) => ({
        title: s.title,
        originalValue: s.uid,
        color: getColor(s.color),
        hasEntityColor: true,
      }));
    }
    return null;
  }

  if (groupBy.property?.type === propertyType.space) {
    return spaces.map((s) => ({
      title: s.title,
      originalValue: s.uid,
      color: getColor(s.color),
      hasEntityColor: true,
    }));
  }

  if (groupBy.property?.type === propertyType.user) {
    return users.map((u) => ({
      title: displayNameOf(u),
      originalValue: u.uid,
      color: getColor(null),
    }));
  }

  if (groupBy.property?.type === propertyType.status) {
    return leafStatuses.map((opt) => ({
      title: textByLang(opt.label, userLang),
      originalValue: opt.uid,
      color: getColor(opt.color),
      hasEntityColor: true,
    }));
  }

  if (groupBy.property?.options) {
    return groupBy.property.options.map((opt) => ({
      title: textByLang(opt.label, userLang),
      originalValue: opt.uid,
      color: getColor(opt.color),
      hasEntityColor: true,
    }));
  }

  return null;
};

const uidMapCache = new WeakMap();
const buildUIDMap = (entities) => {
  const cached = uidMapCache.get(entities);
  if (cached) {
    return cached;
  }
  const map = new Map();
  entities.forEach((e) => map.set(e.uid, e));
  uidMapCache.set(entities, map);
  return map;
};

const findByHexUID = (uidMap, value) => {
  const uid = parseInt(value, 16);
  if (Number.isNaN(uid) || uid === 0) return null;
  return uidMap.has(uid) ? uid : null;
};

// Finds an entity by UID (from new cached format) or by display value (fallback for old format)
const findByUIDOrDisplay = (uidMap, entities, extracted, value, getDisplay) => {
  if (extracted !== null) {
    if (uidMap.has(extracted.uid)) return extracted.uid;
  }
  const searchValue = extracted?.displayValue ?? value;
  const match = entities.find((e) => getDisplay(e) === searchValue);
  return match?.uid ?? null;
};

const resolveSingleValue = (value, groupBy, userLang, context, uidMaps) => {
  const extracted = extractUIDFromCachedValue(value);

  if (groupBy.isProperty === false) {
    if (groupBy.edge === goalConfig.edges.goalCycle) {
      return findByHexUID(uidMaps.goalCycles, value);
    }
    if (groupBy.edgeType === propertyType.user) {
      return findByHexUID(uidMaps.users, value);
    }
    if (groupBy.edgeType === propertyType.space) {
      return findByHexUID(uidMaps.spaces, value);
    }
    return value;
  }

  const type = groupBy.property?.type;
  if (type === propertyType.space) {
    return findByUIDOrDisplay(uidMaps.spaces, context.spaces, extracted, value, (s) => s.title);
  }

  if (type === propertyType.user) {
    return findByUIDOrDisplay(uidMaps.users, context.users, extracted, value, (u) => displayNameOf(u));
  }

  if (type === propertyType.status) {
    return findByUIDOrDisplay(uidMaps.leafStatuses, context.leafStatuses, extracted, value, (opt) => textByLang(opt.label, userLang));
  }

  if (groupBy.property?.options?.length > 0) {
    return findByUIDOrDisplay(uidMaps.options, groupBy.property.options, extracted, value, (opt) => textByLang(opt.label, userLang));
  }

  return value;
};

export const resolveValueToArray = (value, groupBy, userLang, context, uidMaps) => {
  if (value === null || value === undefined) {
    return [];
  }

  // Pre-tagged unassigned entries (callers folding into groupByData) must
  // pass through so groupResult lands them in the UNASSIGNED bucket.
  if (value === UNASSIGNED_VALUE) {
    return [UNASSIGNED_VALUE];
  }

  if (typeof value === 'string' && value.includes(CACHED_VALUE_SEPARATOR)) {
    return value.split(CACHED_VALUE_SEPARATOR)
      .map((part) => resolveSingleValue(part, groupBy, userLang, context, uidMaps))
      .filter((p) => p !== null && p !== undefined);
  }

  const resolved = resolveSingleValue(value, groupBy, userLang, context, uidMaps);
  return (resolved !== null && resolved !== undefined) ? [resolved] : [];
};

const resolveResultData = (result, groupBy, stackOptions, userLang, context) => {
  const hasStackOptions = stackOptions?.groupBy !== chartGroupByOption.none;
  const { goalCycles = [], users = [], leafStatuses = [], spaces = [] } = context;
  const uidMaps = {
    goalCycles: buildUIDMap(goalCycles),
    users: buildUIDMap(users),
    leafStatuses: buildUIDMap(leafStatuses),
    spaces: buildUIDMap(spaces),
    options: buildUIDMap(groupBy.property?.options ?? []),
  };

  return result.flatMap((item) => {
    const resolvedVals = resolveValueToArray(item.val, groupBy, userLang, context, uidMaps);
    if (resolvedVals.length === 0) {
      return [];
    }

    if (!hasStackOptions) {
      return resolvedVals.map((val) => ({ ...item, val }));
    }

    const resolvedStackGroups = resolveValueToArray(item.stackGroup, stackOptions, userLang, context, uidMaps);
    if (resolvedStackGroups.length === 0) {
      return [];
    }

    return resolvedVals.flatMap((val) => resolvedStackGroups.map((stackGroup) => ({ ...item, val, stackGroup })));
  });
};

export const cleanResult = (result, groupBy, stackOptions, userLang, context) => resolveResultData(result, groupBy, stackOptions, userLang, context);

const isEqualCustomBucket = (a, b) => {
  if (a.bucketType !== b.bucketType) {
    return false;
  }
  if (a.bucketType === bucketType.automatic) {
    return true;
  }
  if (a.bucketSize !== b.bucketSize) {
    return false;
  }
  if (a.min !== b.min) {
    return false;
  }
  return a.max === b.max;
};

const isEqualGroupBy = (a, b) => {
  if (a.groupBy !== b.groupBy) {
    return false;
  }
  if (a.groupBy === chartGroupByOption.none) {
    return true;
  }
  if ((a.sortBy ?? null) !== (b.sortBy ?? null)) {
    return false;
  }
  if (!isEqual(a.manualOrder ?? [], b.manualOrder ?? [])) {
    return false;
  }
  if (!isEqual(a.hiddenGroups ?? [], b.hiddenGroups ?? [])) {
    return false;
  }
  if (a.groupBy === chartGroupByOption.bucket && !isEqualCustomBucket(a.customBucket, b.customBucket)) {
    return false;
  }
  if (a.isProperty !== b.isProperty) {
    return false;
  }
  if (a.isProperty) {
    return (a.property?.uid ?? null) === (b.property?.uid ?? null);
  }
  if (a.edge !== b.edge) {
    return false;
  }
  if (a.edgeType !== b.edgeType) {
    return false;
  }
  return true;
};

const isEqualAggregation = (a, b) => {
  if (a.aggregator !== b.aggregator) {
    return false;
  }
  if (a.aggregator === aggregatorType.count) {
    return true;
  }
  if (a.isProperty !== b.isProperty) {
    return false;
  }
  if (a.isProperty) {
    return (a.property?.uid ?? null) === (b.property?.uid ?? null);
  }
  if (a.edge !== b.edge) {
    return false;
  }
  if (a.edgeType !== b.edgeType) {
    return false;
  }
  return true;
};

const isEqualColoringRules = (a, b) => {
  const toArray = (v) => (Array.isArray(v) ? v : []);
  const activeA = toArray(a).filter((rule) => !rule.deletedAt);
  const activeB = toArray(b).filter((rule) => !rule.deletedAt);
  if (activeA.length !== activeB.length) {
    return false;
  }
  const sortByOrder = (rules) => [...rules].sort((x, y) => (x.sortOrder ?? 0) - (y.sortOrder ?? 0));
  const sortedA = sortByOrder(activeA);
  const sortedB = sortByOrder(activeB);
  return sortedA.every((ruleA, i) => {
    const ruleB = sortedB[i];
    return ruleA.uid === ruleB.uid
      && (ruleA.operator || '') === (ruleB.operator || '')
      && Number(ruleA.value) === Number(ruleB.value)
      && (ruleA.color || '') === (ruleB.color || '');
  });
};

const isEqualReferenceLines = (a, b) => {
  const activeA = (a || []).filter((line) => !line.deletedAt);
  const activeB = (b || []).filter((line) => !line.deletedAt);
  if (activeA.length !== activeB.length) {
    return false;
  }
  return activeA.every((lineA, i) => {
    const lineB = activeB[i];
    return lineA.uid === lineB.uid
      && Number(lineA.value) === Number(lineB.value)
      && (lineA.label || '') === (lineB.label || '')
      && (lineA.style || '') === (lineB.style || '')
      && (lineA.color || '') === (lineB.color || '');
  });
};

export const isEqualGridPageTileChart = (a, b) => {
  const equalUid = a.uid === b.uid;
  const equalChartType = a.chartType === b.chartType;
  const equalSource = a.source === b.source;
  const equalFilter = isEqual(a.filter, b.filter);
  const equalQuickFilters = isEqual(a.quickFilters ?? [], b.quickFilters ?? []);
  const equalGoalCycles = equalP({ a: a.goalCycles, b: b.goalCycles });
  const equalGroupBy = isEqualGroupBy(a.groupBy, b.groupBy);
  const equalStackOptions = isEqualGroupBy(a.aggregationConfig.stackOptions, b.aggregationConfig.stackOptions);
  const equalAggregation = isEqualAggregation(a.aggregationConfig.aggregation, b.aggregationConfig.aggregation);
  const equalExcludeNullValues = (a.excludeNullValues ?? false) === (b.excludeNullValues ?? false);
  const equalStacked = (a.stacked ?? true) === (b.stacked ?? true);
  const equalColorScheme = (a.colorScheme ?? gridPageChartColorScheme.automatic) === (b.colorScheme ?? gridPageChartColorScheme.automatic);
  const equalPrecision = (a.precision ?? gridPageChartPrecision.precisionAutomatic) === (b.precision ?? gridPageChartPrecision.precisionAutomatic);
  const equalColor = (a.color ?? optionColor.default) === (b.color ?? optionColor.default);
  const equalColoringMode = (a.coloringMode ?? coloringMode.static) === (b.coloringMode ?? coloringMode.static);
  const equalColoringRules = isEqualColoringRules(a.coloringRules, b.coloringRules);
  const equalTimeSeriesInterval = (a.aggregationConfig.timeSeriesInterval ?? timeSeriesInterval.weekly) === (b.aggregationConfig.timeSeriesInterval ?? timeSeriesInterval.weekly);
  const equalReferenceLines = isEqualReferenceLines(a.referenceLines, b.referenceLines);
  const equalMetricTitle = (a.metricTitle ?? '') === (b.metricTitle ?? '');
  const equalMetricTitleEnabled = (a.metricTitleEnabled ?? false) === (b.metricTitleEnabled ?? false);
  return equalUid && equalChartType && equalSource && equalFilter && equalQuickFilters && equalGoalCycles && equalGroupBy && equalStackOptions && equalAggregation && equalExcludeNullValues && equalStacked && equalColorScheme && equalPrecision && equalColor && equalColoringMode && equalColoringRules && equalTimeSeriesInterval && equalReferenceLines && equalMetricTitle && equalMetricTitleEnabled;
};

const DASH_STYLE_MAP = { solid: 'Solid', dash: 'Dash', dot: 'Dot' };

const getReferenceLineColor = (color) => colors[color]?.darken1 ?? colors.grey.darken1;

export const mapReferenceLinesToPlotLines = (referenceLines, { min, max } = {}) => {
  if (!Array.isArray(referenceLines) || referenceLines.length === 0) {
    return [];
  }
  const hasMin = min !== undefined && min !== null;
  const hasMax = max !== undefined && max !== null;
  return referenceLines.reduce((acc, line) => {
    if (line.value === null || line.value === undefined) return acc;
    const value = Number(line.value);
    if (hasMax && value > max) return acc;
    if (hasMin && value < min) return acc;
    acc.push({
      value,
      color: getReferenceLineColor(line.color || 'grey'),
      dashStyle: DASH_STYLE_MAP[line.style] || 'Solid',
      width: 1,
      zIndex: 5,
      label: line.label ? {
        text: line.label,
        align: 'left',
        style: { color: colors.grey.darken1, fontWeight: '600', fontSize: '1.2rem' },
      } : undefined,
    });
    return acc;
  }, []);
};

const REF_LINE_ID_PREFIX = 'mc-ref-line-';
const REF_LINE_STATE_KEY = '__mcRefLineSig';

export const syncReferenceLinesToAxis = (chartInstance, referenceLines) => {
  const yAxis = chartInstance?.yAxis?.[0];
  if (!yAxis) return;
  const { min, max } = yAxis.getExtremes();
  // A line exactly at max gets clipped by the plot area's top edge, so keep a
  // small buffer below max (1% of the visible range) to ensure it renders.
  const buffer = (max - min) * 0.01;
  const lines = mapReferenceLinesToPlotLines(referenceLines, { min, max: max - buffer });
  const signature = JSON.stringify(lines);
  if (chartInstance[REF_LINE_STATE_KEY] === signature) return;
  chartInstance[REF_LINE_STATE_KEY] = signature;
  const existing = (yAxis.plotLinesAndBands ?? []).filter((p) => typeof p.id === 'string' && p.id.startsWith(REF_LINE_ID_PREFIX));
  existing.forEach((p) => yAxis.removePlotLine(p.id));
  lines.forEach((pl, i) => yAxis.addPlotLine({ ...pl, id: `${REF_LINE_ID_PREFIX}${i}` }));
};

export const generateDateRange = (start, end, groupBy) => {
  const result = [];

  switch (groupBy) {
    case chartGroupByOption.day: {
      const startDate = new Date(start);
      const endDate = new Date(end);
      const current = new Date(startDate);
      while (current <= endDate) {
        result.push(current.toISOString().split('T')[0]);
        current.setDate(current.getDate() + 1);
      }
      break;
    }
    case chartGroupByOption.week: {
      const [startYear, startWeekStr] = start.split('-W');
      const [endYear, endWeekStr] = end.split('-W');
      let currentYear = parseInt(startYear, 10);
      let currentWeek = parseInt(startWeekStr, 10);
      const finalYear = parseInt(endYear, 10);
      const finalWeek = parseInt(endWeekStr, 10);

      while (currentYear < finalYear || (currentYear === finalYear && currentWeek <= finalWeek)) {
        result.push(`${currentYear}-W${currentWeek.toString().padStart(2, '0')}`);
        currentWeek += 1;
        const weeksInYear = getISOWeeksInYear(currentYear);
        if (currentWeek > weeksInYear) {
          currentWeek = 1;
          currentYear += 1;
        }
      }
      break;
    }
    case chartGroupByOption.month: {
      const [startYear, startMonth] = start.split('-').map(Number);
      const [endYear, endMonth] = end.split('-').map(Number);
      let currentYear = startYear;
      let currentMonth = startMonth;

      while (currentYear < endYear || (currentYear === endYear && currentMonth <= endMonth)) {
        result.push(`${currentYear}-${currentMonth.toString().padStart(2, '0')}`);
        currentMonth += 1;
        if (currentMonth > 12) {
          currentMonth = 1;
          currentYear += 1;
        }
      }
      break;
    }
    case chartGroupByOption.quarter: {
      const [startYear, startQ] = start.split('-Q').map(Number);
      const [endYear, endQ] = end.split('-Q').map(Number);
      let currentYear = startYear;
      let currentQuarter = startQ;

      while (currentYear < endYear || (currentYear === endYear && currentQuarter <= endQ)) {
        result.push(`${currentYear}-Q${currentQuarter}`);
        currentQuarter += 1;
        if (currentQuarter > 4) {
          currentQuarter = 1;
          currentYear += 1;
        }
      }
      break;
    }
    case chartGroupByOption.year: {
      const startYear = typeof start === 'string' ? parseInt(start, 10) : start;
      const endYear = typeof end === 'string' ? parseInt(end, 10) : end;
      for (let year = startYear; year <= endYear; year += 1) {
        result.push(`${year}`);
      }
      break;
    }
    default:
      break;
  }

  return result;
};

// ISO 8601: years with 53 weeks start or end on Thursday
const getISOWeeksInYear = (year) => {
  const jan1 = new Date(year, 0, 1);
  const dec31 = new Date(year, 11, 31);
  return (jan1.getDay() === 4 || dec31.getDay() === 4) ? 53 : 52;
};

export const escapeHtml = (str) => String(str).replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;')
  .replace(/"/g, '&quot;');

export const dataLabelStyle = {
  color: colors.grey.darken1,
  fontSize: '12px',
  fontWeight: 'normal',
  textOutline: 'none',
};

const tooltipCardStyle = 'background:white;border-radius:8px;padding:10px 12px;font-size:12px;box-shadow:rgb(38 50 56/10%) 0 0 0 1px,rgb(38 50 56/10%) 0 2px 4px;display:flex;flex-direction:column;gap:3px';
const tooltipRowStyle = 'display:flex;align-items:center;gap:.6rem';

export const formatTooltipCard = (content) => `<div style="${tooltipCardStyle}">${content}</div>`;

export const formatTooltipRow = (color, name, value) => `<div style="${tooltipRowStyle}">
    <span style="width:8px;height:8px;border-radius:2px;flex-shrink:0;background:${color}"></span>
    <span style="flex:1;margin-right:1.2rem">${escapeHtml(name)}</span>
    <span style="color:${colors.grey.lighten1}">${escapeHtml(value)}</span>
  </div>`;

export const formatNoData = (label) => `<div style="color:${colors.grey.base}">${escapeHtml(label)}</div>`;

export const computeLabelStep = (categoryCount, maxLabels = 8) => {
  if (categoryCount <= maxLabels) {
    return 1;
  }
  return Math.ceil(categoryCount / maxLabels);
};

export const nudgeFirstLabel = (chart) => {
  const catAxis = chart.inverted ? chart.yAxis[0] : chart.xAxis[0];
  if (!catAxis?.horiz || catAxis.tickPositions?.length < 2) return;
  if ((catAxis.tickInterval ?? 1) <= 1) return;

  const firstTick = catAxis.ticks[catAxis.tickPositions[0]];
  if (!firstTick?.label?.element) return;

  const chartLeft = chart.container.getBoundingClientRect().left;
  const labelRect = firstTick.label.element.getBoundingClientRect();

  if (labelRect.left >= chartLeft) return;

  // Convert between viewport and SVG coordinates using the known label position
  const svgX = firstTick.label.attr('x');
  const vpToSvg = svgX - (labelRect.left + labelRect.width / 2);
  const chartLeftSvg = chartLeft + vpToSvg;

  // Check if nudging would overlap with the second visible label
  const secondTick = catAxis.ticks[catAxis.tickPositions[1]];
  if (secondTick?.label?.element) {
    const secondLeft = secondTick.label.element.getBoundingClientRect().left;
    const gap = 8;
    const nudgedRight = labelRect.right + (chartLeft - labelRect.left);

    if (nudgedRight > secondLeft - gap) {
      // Truncate: left-align at chart edge, constrain width to avoid overlap
      const maxWidth = secondLeft - chartLeft - gap;
      firstTick.label.css({ width: `${maxWidth}px`, textOverflow: 'ellipsis', whiteSpace: 'nowrap' });
      firstTick.label.attr({ x: chartLeftSvg + maxWidth / 2 });
      return;
    }
  }

  // Simple nudge — no overlap
  const overflow = chartLeft - labelRect.left;
  firstTick.label.attr({ x: svgX + overflow });
};

export const UNASSIGNED_VALUE = '__unassigned__';

export const CACHED_VALUE_SEPARATOR = '___COMMA___';
export const CACHED_UID_SEPARATOR = '___UID___';

export const extractUIDFromCachedValue = (value) => {
  if (typeof value !== 'string' || !value.includes(CACHED_UID_SEPARATOR)) {
    return null;
  }
  const parts = value.split(CACHED_UID_SEPARATOR);
  if (parts.length !== 2) {
    return null;
  }
  const uid = parseInt(parts[1], 10);
  if (Number.isNaN(uid) || uid === 0) {
    return null;
  }
  return { displayValue: parts[0], uid };
};

export const getDisplayValue = (cachedValue, entity, groupBy, userLang) => {
  if (cachedValue === null || cachedValue === undefined) {
    return '';
  }
  if (isDateGrouping(groupBy.groupBy)) {
    switch (groupBy.groupBy) {
      case chartGroupByOption.day:
        return new Date(cachedValue).toLocaleDateString(userLang, { day: 'numeric', month: 'short', year: 'numeric', timeZone: 'UTC' });
      case chartGroupByOption.week: {
        // ISO 8601: week 1 contains the first Thursday, weeks start Monday
        const [year, weekStr] = cachedValue.split('-W');
        const week = parseInt(weekStr, 10);
        const yearStart = new Date(parseInt(year, 10), 0, 1);
        const dayOffset = yearStart.getDay() || 7;
        const mondayOffset = dayOffset <= 4 ? 1 - dayOffset : 8 - dayOffset;
        const firstMonday = new Date(yearStart);
        firstMonday.setDate(yearStart.getDate() + mondayOffset);
        const startDate = new Date(firstMonday);
        startDate.setDate(firstMonday.getDate() + (week - 1) * 7);
        const endDate = new Date(startDate);
        endDate.setDate(startDate.getDate() + 6);
        const sameMonth = startDate.getMonth() === endDate.getMonth();
        if (sameMonth) {
          return `${startDate.toLocaleDateString(userLang, { month: 'short', day: 'numeric' })} – ${endDate.getDate()}, ${endDate.getFullYear()}`;
        }
        return `${startDate.toLocaleDateString(userLang, { month: 'short', day: 'numeric' })} – ${endDate.toLocaleDateString(userLang, { month: 'short', day: 'numeric' })}, ${endDate.getFullYear()}`;
      }
      case chartGroupByOption.month:
        return new Date(cachedValue).toLocaleDateString(userLang, { month: 'short', year: 'numeric', timeZone: 'UTC' });
      case chartGroupByOption.quarter: {
        const [year, quarter] = cachedValue.split('-Q');
        return `Q${quarter} ${year}`;
      }
      case chartGroupByOption.year:
        return new Date(cachedValue).toLocaleDateString(userLang, { year: 'numeric', timeZone: 'UTC' });
      default:
        return cachedValue;
    }
  }
  if (entity === undefined || entity === null) {
    return cachedValue;
  }
  if (groupBy.isProperty === false) {
    if (groupBy.edgeType === propertyType.user) {
      return displayNameOf(entity);
    }
    if (groupBy.edgeType === propertyType.space || groupBy.edge === goalConfig.edges.goalCycle) {
      return entity.title;
    }
    return cachedValue;
  }
  switch (groupBy.property?.type) {
    case propertyType.user:
      return displayNameOf(entity);
    case propertyType.space:
      return entity.title;
    case propertyType.status:
    case propertyType.singleSelect:
    case propertyType.options:
      return textByLang(entity.label, userLang);
    default:
      return cachedValue;
  }
};
